/**
 * Contacts (recipients) data hooks — PEL-822.
 *
 * Thin TanStack Query wrappers over `@pelipost/consumer-api-client` contacts
 * module. Screens never call `api.contacts.*` directly — they use these hooks
 * so cache invalidation + optimistic behaviour stay consistent.
 *
 * Cache keys are stable and grouped so mutations can invalidate precisely:
 *   ['contacts']                       — full list
 *   ['contacts', id]                   — single contact
 *   ['contacts', id, 'attributes']     — attribute values for a contact
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type {
  Contact,
  ContactAttributeValue,
  CreateContactRequest,
  UpsertContactAttributesRequest,
} from '@pelipost/consumer-api-client';
import { api } from '../lib/api/client';

export const contactsKeys = {
  all: ['contacts'] as const,
  detail: (id: string) => ['contacts', id] as const,
  attributes: (id: string) => ['contacts', id, 'attributes'] as const,
};

/** List all contacts for the signed-in user. */
export function useContactsList(
  options?: Omit<UseQueryOptions<Contact[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Contact[]>({
    queryKey: contactsKeys.all,
    queryFn: () => api.contacts.getContacts(),
    ...options,
  });
}

/** Fetch a single contact by id. Disabled when id is empty. */
export function useContact(id: string | undefined) {
  return useQuery<Contact>({
    queryKey: id ? contactsKeys.detail(id) : ['contacts', 'disabled'],
    queryFn: () => api.contacts.getContact(id as string),
    enabled: Boolean(id),
  });
}

/** Fetch attribute values saved on a contact. */
export function useContactAttributes(contactId: string | undefined) {
  return useQuery<ContactAttributeValue[]>({
    queryKey: contactId
      ? contactsKeys.attributes(contactId)
      : ['contacts', 'disabled', 'attributes'],
    queryFn: () => api.contacts.getContactAttributes(contactId as string),
    enabled: Boolean(contactId),
  });
}

/** Create a new contact. Invalidates the list so the UI picks it up. */
export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation<Contact, Error, CreateContactRequest>({
    mutationFn: (data) => api.contacts.createContact(data),
    onSuccess: (contact) => {
      qc.invalidateQueries({ queryKey: contactsKeys.all });
      qc.setQueryData(contactsKeys.detail(contact.id), contact);
    },
  });
}

/** Update an existing contact. */
export function useUpdateContact(id: string) {
  const qc = useQueryClient();
  return useMutation<Contact, Error, Partial<CreateContactRequest & { nickname?: string }>>({
    mutationFn: (data) => api.contacts.updateContact(id, data),
    onSuccess: (contact) => {
      qc.setQueryData(contactsKeys.detail(id), contact);
      qc.invalidateQueries({ queryKey: contactsKeys.all });
    },
  });
}

/** Delete a contact. Invalidates list and removes the cached detail. */
export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.contacts.deleteContact(id),
    onSuccess: (_void, id) => {
      qc.removeQueries({ queryKey: contactsKeys.detail(id) });
      qc.removeQueries({ queryKey: contactsKeys.attributes(id) });
      qc.invalidateQueries({ queryKey: contactsKeys.all });
    },
  });
}

/** Save attribute values for a contact. */
export function useUpsertContactAttributes(contactId: string) {
  const qc = useQueryClient();
  return useMutation<ContactAttributeValue[], Error, UpsertContactAttributesRequest>({
    mutationFn: (data) => api.contacts.upsertContactAttributes(contactId, data),
    onSuccess: (values) => {
      qc.setQueryData(contactsKeys.attributes(contactId), values);
    },
  });
}
